import React, { useState, useEffect } from "react";

const API_URL = "https://crm-backend-6jqe.onrender.com/api";

export default function TransferHistory({ isDarkMode, onClose }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    transferType: "",
    fromUser: "",
    toUser: "",
    companyName: ""
  });
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    loadTransfers();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/users?userId=${localStorage.getItem('lastUserId') || ''}`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Chyba při načítání uživatelů:', err);
    }
  };

  const loadTransfers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Sestavení query parametrů pro filtrování
      const queryParams = new URLSearchParams();
      queryParams.append('userId', localStorage.getItem('lastUserId') || '');
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      if (filters.transferType) queryParams.append('transferType', filters.transferType);
      if (filters.fromUser) queryParams.append('fromUser', filters.fromUser);
      if (filters.toUser) queryParams.append('toUser', filters.toUser);
      if (filters.companyName) queryParams.append('companyName', filters.companyName);
      
      const res = await fetch(`${API_URL}/company-transfers?${queryParams.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setTransfers(data);
      } else {
        setError('Chyba při načítání historie přesunů');
      }
    } catch (err) {
      console.error('Chyba při načítání historie:', err);
      setError('Chyba při načítání historie přesunů');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    loadTransfers();
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      transferType: "",
      fromUser: "",
      toUser: "",
      companyName: ""
    });
    setCurrentPage(1);
    // Načteme všechny transfery
    setTimeout(() => loadTransfers(), 100);
  };

  const getTransferTypeLabel = (type) => {
    switch (type) {
      case 'assignment': return '📤 Přidělení';
      case 'self_assignment': return '👤 Samo-přidělení';
      case 'admin_transfer': return '⚙️ Admin přesun';
      case 'import': return '📥 Import';
      default: return type;
    }
  };

  const getTransferTypeColor = (type) => {
    switch (type) {
      case 'assignment': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'self_assignment': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'admin_transfer': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'import': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  // Stránkování
  const totalPages = Math.ceil(transfers.length / itemsPerPage);
  const pagedTransfers = transfers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div style={{
      position: "fixed", 
      left: 0, 
      top: 0, 
      width: "100vw", 
      height: "100vh",
      background: "rgba(0,0,0,0.5)", 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center", 
      zIndex: 1000
    }}>
      <div className={`w-full max-w-7xl h-[90vh] mx-4 p-6 rounded-lg shadow-lg overflow-hidden flex flex-col ${
        isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
      }`}>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-shrink-0">
          <h2 className="text-2xl font-bold">📊 Historie přesunů firem</h2>
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition"
          >
            ✕ Zavřít
          </button>
        </div>

        {/* Filtry */}
        <div className={`p-4 rounded-lg mb-6 flex-shrink-0 ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <h3 className="text-lg font-semibold mb-3">🔍 Filtry</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Datum od</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Datum do</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Typ přesunu</label>
              <select
                value={filters.transferType}
                onChange={(e) => handleFilterChange('transferType', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">Všechny typy</option>
                <option value="assignment">Přidělení</option>
                <option value="self_assignment">Samo-přidělení</option>
                <option value="admin_transfer">Admin přesun</option>
                <option value="import">Import</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Od uživatele</label>
              <select
                value={filters.fromUser}
                onChange={(e) => handleFilterChange('fromUser', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">Všichni uživatelé</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>{user.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Pro uživatele</label>
              <select
                value={filters.toUser}
                onChange={(e) => handleFilterChange('toUser', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">Všichni uživatelé</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>{user.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Název firmy</label>
              <input
                type="text"
                placeholder="Hledat podle názvu..."
                value={filters.companyName}
                onChange={(e) => handleFilterChange('companyName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${
                  isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={applyFilters}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition"
            >
              🔍 Aplikovat filtry
            </button>
            <button
              onClick={clearFilters}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold transition"
            >
              🗑️ Vymazat filtry
            </button>
          </div>
        </div>

        {/* Obsah */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p>Načítám historii přesunů...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-red-600">
                <p className="text-xl mb-2">❌</p>
                <p>{error}</p>
              </div>
            </div>
          ) : transfers.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <p className="text-4xl mb-2">📋</p>
                <p>Žádné záznamy o přesunech</p>
              </div>
            </div>
          ) : (
            <>
              {/* Tabulka */}
              <div className="flex-1 overflow-auto">
                <div className={`rounded-lg shadow overflow-hidden ${
                  isDarkMode ? 'bg-gray-700' : 'bg-white'
                }`}>
                  <table className="min-w-full">
                    <thead className={`${
                      isDarkMode ? 'bg-gray-600' : 'bg-gray-50'
                    }`}>
                      <tr>
                        <th className="py-3 px-4 text-left font-semibold">Datum</th>
                        <th className="py-3 px-4 text-left font-semibold">Firma</th>
                        <th className="py-3 px-4 text-left font-semibold">IČO</th>
                        <th className="py-3 px-4 text-left font-semibold">Od uživatele</th>
                        <th className="py-3 px-4 text-left font-semibold">Pro uživatele</th>
                        <th className="py-3 px-4 text-left font-semibold">Typ přesunu</th>
                        <th className="py-3 px-4 text-left font-semibold">Iniciátor</th>
                        <th className="py-3 px-4 text-left font-semibold">Poznámka</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${
                      isDarkMode ? 'divide-gray-600' : 'divide-gray-200'
                    }`}>
                      {pagedTransfers.map((transfer) => (
                        <tr key={transfer._id} className={`${
                          isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-50'
                        }`}>
                          <td className="py-3 px-4 text-sm">
                            {new Date(transfer.transferDate).toLocaleString('cs-CZ')}
                          </td>
                          <td className="py-3 px-4 font-medium">
                            {transfer.companyName}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {transfer.companyIco}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {transfer.fromUserName || 'Nový kontakt'}
                          </td>
                          <td className="py-3 px-4 text-sm font-medium">
                            {transfer.toUserName}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              getTransferTypeColor(transfer.transferType)
                            }`}>
                              {getTransferTypeLabel(transfer.transferType)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {transfer.initiatedByName}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {transfer.note || transfer.batchName || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Stránkování */}
              <div className="flex items-center justify-between mt-4 flex-shrink-0">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Zobrazeno {Math.min((currentPage - 1) * itemsPerPage + 1, transfers.length)}-{Math.min(currentPage * itemsPerPage, transfers.length)} z {transfers.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className={`px-4 py-2 rounded-lg font-semibold transition ${
                      currentPage === 1 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-gray-600 hover:bg-gray-700 text-white'
                    }`}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    ← Předchozí
                  </button>
                  <span className={`px-3 py-2 text-sm font-medium rounded-lg ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    className={`px-4 py-2 rounded-lg font-semibold transition ${
                      currentPage === totalPages 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-gray-600 hover:bg-gray-700 text-white'
                    }`}
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Další →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
